/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService, NotifyService } from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable } from 'rxjs';
import { SysControlEnum } from '../enums';
import { UpdateMajorPackageModel, UpdateManualModel, UpdateModel, UpdatePackageModel } from '../models';

@Injectable()
export class SystemApiService {
    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService,
        private notifyService: NotifyService
    ) {
    }

    /**
     * Reboot the box
     * @returns {Observable<any>}
     */
    public restart(options?: any): Observable<any> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(
                    SysControlEnum.PUT_SYSTEM_REBOOT,
                    null,
                    moduleAuth,
                    true
                ).subscribe(
                    (response: any) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Shutdown the box
     * @param options
     */
    public shutdown(options?: any): Observable<any> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(
                    SysControlEnum.PUT_SYSTEM_HALT,
                    null,
                    moduleAuth,
                    true
                ).subscribe(
                    (response: any) => {
                        this.notifyService.warning('The box is now shutting down.');
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Get available updates
     * @param options
     */
    public getUpdates(options?: any): Observable<UpdateModel> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                const url = options?.force && options?.hasForcePermissions
                    ? SysControlEnum.GET_SYSTEM_UPDATE_STATUS_FORCE
                    : SysControlEnum.GET_SYSTEM_UPDATE_STATUS;

                this.apiService.get(
                    url,
                    moduleAuth,
                    true,
                    options
                ).subscribe(
                    (response: UpdateModel) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Api call to update the system's major version.
     * @param data
     * @param options
     */
    public updateMajorVersion(data: any, options?: any): Observable<UpdateMajorPackageModel> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(
                    SysControlEnum.PUT_SYSTEM_UPDATE_MAJOR,
                    data,
                    moduleAuth,
                    true,
                    { timeout: 20000 }
                ).subscribe(
                    (response: UpdateMajorPackageModel) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Api call to update the system
     * @param options
     */
    public updatePackages(options?: any): Observable<UpdatePackageModel[]> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(
                    SysControlEnum.PUT_SYSTEM_UPDATE,
                    null,
                    moduleAuth,
                    true,
                    options
                ).subscribe(
                    (response: any) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Api call to update the system
     * @param file
     * @param options
     */
    public manualUpdate(file: File, options?: any): Observable<UpdateManualModel> {
        const uploadData = new FormData();
        uploadData.append('packages', file, file.name);

        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.post(
                    SysControlEnum.POST_SYSTEM_UPDATE,
                    uploadData,
                    moduleAuth,
                    true
                ).subscribe(
                    (response: UpdateManualModel) => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Get system stats
     * @param params
     * @param options
     */
    public getSystemStats(params: string, options?: any): Observable<any> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(SysControlEnum.GET_SYSTEM_STATS(params), moduleAuth)
                    .subscribe(
                        (response: any) => {
                            observer.next(response);
                            observer.complete();
                        },
                        (error: ErrorResponseModel) => {
                            observer.error(error);
                            observer.complete();
                        }
                    );
            });
    }
}
